import React, { useState } from 'react';
import { BellIcon, ShieldIcon, KeyIcon, GlobeIcon, CreditCardIcon, UserIcon, MailIcon, SmartphoneIcon, CheckIcon, XIcon, PlusIcon, TrashIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
type TabType = 'profile' | 'billing' | 'privacy' | 'security' | 'notifications';
export const Settings = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Profile state
  const [profileData, setProfileData] = useState({
    displayName: 'John Doe',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    bio: 'AI enthusiast and developer',
    location: 'San Francisco, CA',
    website: 'https://johndoe.com'
  });
  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showApps: true,
    allowMessages: true,
    dataSharing: false,
    analytics: true
  });
  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
    sessionTimeout: '30'
  });
  // Notification state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    appUpdates: true,
    newFeatures: true,
    marketingEmails: false,
    weeklyDigest: true,
    securityAlerts: true
  });
  const tabs = [{
    id: 'profile' as TabType,
    label: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-1205" />
  }, {
    id: 'billing' as TabType,
    label: 'Billing',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-1206" />
  }, {
    id: 'privacy' as TabType,
    label: 'Privacy',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-1207" />
  }, {
    id: 'security' as TabType,
    label: 'Security',
    icon: <KeyIcon className="h-5 w-5" data-id="element-1208" />
  }, {
    id: 'notifications' as TabType,
    label: 'Notifications',
    icon: <BellIcon className="h-5 w-5" data-id="element-1209" />
  }];
  const paymentMethods = [{
    id: '1',
    type: 'Visa',
    last4: '4242',
    expiry: '12/25',
    isDefault: true
  }, {
    id: '2',
    type: 'Mastercard',
    last4: '8888',
    expiry: '09/24',
    isDefault: false
  }];
  const billingHistory = [{
    id: '1',
    date: 'June 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-001'
  }, {
    id: '2',
    date: 'May 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-002'
  }, {
    id: '3',
    date: 'April 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-003'
  }];
  const activeSessions = [{
    id: '1',
    device: 'Chrome on MacBook Pro',
    location: 'San Francisco, CA',
    lastActive: '2 minutes ago',
    current: true
  }, {
    id: '2',
    device: 'Safari on iPhone 14',
    location: 'San Francisco, CA',
    lastActive: '1 hour ago',
    current: false
  }];
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-1210">
      <div className="max-w-6xl mx-auto" data-id="element-1211">
        <h1 className="text-2xl font-bold mb-8" data-id="element-1212">Settings</h1>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-1213">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0" data-id="element-1214">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1215">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1216">
                  {tab.icon}
                  <span className="ml-3 font-medium" data-id="element-1217">{tab.label}</span>
                </button>)}
            </div>
          </div>
          {/* Content Area */}
          <div className="flex-1" data-id="element-1218">
            {/* Profile Tab */}
            {activeTab === 'profile' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1219">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1220">Profile Settings</h2>
                <div className="space-y-6" data-id="element-1221">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1222">
                    <div data-id="element-1223">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1224">
                        Display Name
                      </label>
                      <input type="text" value={profileData.displayName} onChange={e => setProfileData({
                    ...profileData,
                    displayName: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1225" />
                    </div>
                    <div data-id="element-1226">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1227">
                        Email Address
                      </label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1228" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1229">
                    <div data-id="element-1230">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1231">
                        Phone Number
                      </label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({
                    ...profileData,
                    phone: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1232" />
                    </div>
                    <div data-id="element-1233">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1234">
                        Location
                      </label>
                      <input type="text" value={profileData.location} onChange={e => setProfileData({
                    ...profileData,
                    location: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1235" />
                    </div>
                  </div>
                  <div data-id="element-1236">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1237">
                      Website
                    </label>
                    <input type="url" value={profileData.website} onChange={e => setProfileData({
                  ...profileData,
                  website: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1238" />
                  </div>
                  <div data-id="element-1239">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1240">
                      Bio
                    </label>
                    <textarea value={profileData.bio} onChange={e => setProfileData({
                  ...profileData,
                  bio: e.target.value
                })} rows={4} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1241" />
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1242">
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1243">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1244">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Billing Tab */}
            {activeTab === 'billing' && <div className="space-y-6" data-id="element-1245">
                {/* Payment Methods */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1246">
                  <div className="flex justify-between items-center mb-6" data-id="element-1247">
                    <h2 className="text-xl font-semibold" data-id="element-1248">Payment Methods</h2>
                    <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1249">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-1250" />
                      Add Method
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-1251">
                    {paymentMethods.map(method => <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-1252">
                        <div className="flex items-center" data-id="element-1253">
                          <CreditCardIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-1254" />
                          <div data-id="element-1255">
                            <div className="font-medium" data-id="element-1256">
                              {method.type} •••• {method.last4}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1257">
                              Expires {method.expiry}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-1258">
                          {method.isDefault && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1259">
                              Default
                            </span>}
                          <button className="p-2 text-gray-400 hover:text-red-600" data-id="element-1260">
                            <TrashIcon className="h-4 w-4" data-id="element-1261" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Billing History */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1262">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1263">
                    Billing History
                  </h2>
                  <div className="overflow-x-auto" data-id="element-1264">
                    <table className="w-full" data-id="element-1265">
                      <thead data-id="element-1266">
                        <tr className="border-b border-gray-200" data-id="element-1267">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1268">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1269">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1270">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1271">
                            Invoice
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1272">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1273">
                        {billingHistory.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-1274">
                            <td className="py-3 px-4 text-sm" data-id="element-1275">{item.date}</td>
                            <td className="py-3 px-4 text-sm font-medium" data-id="element-1276">
                              {item.amount}
                            </td>
                            <td className="py-3 px-4" data-id="element-1277">
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1278">
                                {item.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500" data-id="element-1279">
                              {item.invoice}
                            </td>
                            <td className="py-3 px-4 text-right" data-id="element-1280">
                              <button className="text-sm text-indigo-600 hover:text-indigo-700" data-id="element-1281">
                                Download
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
            {/* Privacy Tab */}
            {activeTab === 'privacy' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1282">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1283">Privacy Settings</h2>
                <div className="space-y-6" data-id="element-1284">
                  <div data-id="element-1285">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1286">
                      Profile Visibility
                    </label>
                    <select value={privacySettings.profileVisibility} onChange={e => setPrivacySettings({
                  ...privacySettings,
                  profileVisibility: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1287">
                      <option value="public" data-id="element-1288">Public</option>
                      <option value="private" data-id="element-1289">Private</option>
                      <option value="friends" data-id="element-1290">Friends Only</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-1291">
                      Control who can see your profile
                    </p>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-1292">
                    <label className="flex items-center justify-between" data-id="element-1293">
                      <div data-id="element-1294">
                        <div className="font-medium" data-id="element-1295">Show Email Address</div>
                        <div className="text-sm text-gray-500" data-id="element-1296">
                          Display your email on your profile
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showEmail: !privacySettings.showEmail
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showEmail ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1297">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showEmail ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1298" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1299">
                      <div data-id="element-1300">
                        <div className="font-medium" data-id="element-1301">Show My Apps</div>
                        <div className="text-sm text-gray-500" data-id="element-1302">
                          Display your created apps publicly
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showApps: !privacySettings.showApps
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showApps ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1303">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showApps ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1304" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1305">
                      <div data-id="element-1306">
                        <div className="font-medium" data-id="element-1307">Allow Messages</div>
                        <div className="text-sm text-gray-500" data-id="element-1308">
                          Let other users send you messages
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    allowMessages: !privacySettings.allowMessages
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.allowMessages ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1309">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.allowMessages ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1310" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1311">
                      <div data-id="element-1312">
                        <div className="font-medium" data-id="element-1313">Data Sharing</div>
                        <div className="text-sm text-gray-500" data-id="element-1314">
                          Share usage data to improve services
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    dataSharing: !privacySettings.dataSharing
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.dataSharing ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1315">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.dataSharing ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1316" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1317">
                      <div data-id="element-1318">
                        <div className="font-medium" data-id="element-1319">Analytics</div>
                        <div className="text-sm text-gray-500" data-id="element-1320">
                          Help us improve with anonymous analytics
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    analytics: !privacySettings.analytics
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.analytics ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1321">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.analytics ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1322" />
                      </button>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1323">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1324">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Security Tab */}
            {activeTab === 'security' && <div className="space-y-6" data-id="element-1325">
                {/* Change Password */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1326">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1327">
                    Change Password
                  </h2>
                  <div className="space-y-4" data-id="element-1328">
                    <div data-id="element-1329">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1330">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-1331">
                        <input type={showPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1332" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-1333">
                          {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1334" /> : <EyeIcon className="h-5 w-5" data-id="element-1335" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1336">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1337">
                        New Password
                      </label>
                      <div className="relative" data-id="element-1338">
                        <input type={showNewPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1339" />
                        <button onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-1340">
                          {showNewPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1341" /> : <EyeIcon className="h-5 w-5" data-id="element-1342" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1343">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1344">
                        Confirm New Password
                      </label>
                      <input type="password" className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1345" />
                    </div>
                    <div className="flex justify-end pt-2" data-id="element-1346">
                      <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1347">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                {/* Two-Factor Authentication */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1348">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1349">
                    Two-Factor Authentication
                  </h2>
                  <div className="space-y-4" data-id="element-1350">
                    <label className="flex items-center justify-between" data-id="element-1351">
                      <div data-id="element-1352">
                        <div className="font-medium" data-id="element-1353">Enable 2FA</div>
                        <div className="text-sm text-gray-500" data-id="element-1354">
                          Add an extra layer of security to your account
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    twoFactorEnabled: !securitySettings.twoFactorEnabled
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.twoFactorEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1355">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1356" />
                      </button>
                    </label>
                    {securitySettings.twoFactorEnabled && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1357">
                        <div className="flex items-start" data-id="element-1358">
                          <AlertCircleIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" data-id="element-1359" />
                          <div data-id="element-1360">
                            <p className="text-sm text-blue-900 font-medium" data-id="element-1361">
                              2FA is enabled
                            </p>
                            <p className="text-sm text-blue-700 mt-1" data-id="element-1362">
                              You will need to enter a verification code when
                              logging in
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Security Preferences */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1363">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1364">
                    Security Preferences
                  </h2>
                  <div className="space-y-4" data-id="element-1365">
                    <label className="flex items-center justify-between" data-id="element-1366">
                      <div data-id="element-1367">
                        <div className="font-medium" data-id="element-1368">Login Alerts</div>
                        <div className="text-sm text-gray-500" data-id="element-1369">
                          Get notified of new login attempts
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    loginAlerts: !securitySettings.loginAlerts
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.loginAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1370">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1371" />
                      </button>
                    </label>
                    <div data-id="element-1372">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1373">
                        Session Timeout
                      </label>
                      <select value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
                    ...securitySettings,
                    sessionTimeout: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1374">
                        <option value="15" data-id="element-1375">15 minutes</option>
                        <option value="30" data-id="element-1376">30 minutes</option>
                        <option value="60" data-id="element-1377">1 hour</option>
                        <option value="120" data-id="element-1378">2 hours</option>
                      </select>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-1379">
                        Automatically log out after inactivity
                      </p>
                    </div>
                  </div>
                </div>
                {/* Active Sessions */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1380">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1381">
                    Active Sessions
                  </h2>
                  <div className="space-y-4" data-id="element-1382">
                    {activeSessions.map(session => <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-1383">
                        <div className="flex items-center" data-id="element-1384">
                          <SmartphoneIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-1385" />
                          <div data-id="element-1386">
                            <div className="font-medium flex items-center" data-id="element-1387">
                              {session.device}
                              {session.current && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1388">
                                  Current
                                </span>}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1389">
                              {session.location} • {session.lastActive}
                            </div>
                          </div>
                        </div>
                        {!session.current && <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md" data-id="element-1390">
                            Revoke
                          </button>}
                      </div>)}
                  </div>
                </div>
              </div>}
            {/* Notifications Tab */}
            {activeTab === 'notifications' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1391">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1392">
                  Notification Preferences
                </h2>
                <div className="space-y-4" data-id="element-1393">
                  <label className="flex items-center justify-between" data-id="element-1394">
                    <div data-id="element-1395">
                      <div className="font-medium" data-id="element-1396">Email Notifications</div>
                      <div className="text-sm text-gray-500" data-id="element-1397">
                        Receive notifications via email
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  emailNotifications: !notificationSettings.emailNotifications
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1398">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1399" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1400">
                    <div data-id="element-1401">
                      <div className="font-medium" data-id="element-1402">App Updates</div>
                      <div className="text-sm text-gray-500" data-id="element-1403">
                        Get notified when your apps are updated
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  appUpdates: !notificationSettings.appUpdates
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.appUpdates ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1404">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.appUpdates ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1405" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1406">
                    <div data-id="element-1407">
                      <div className="font-medium" data-id="element-1408">New Features</div>
                      <div className="text-sm text-gray-500" data-id="element-1409">
                        Learn about new platform features
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  newFeatures: !notificationSettings.newFeatures
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.newFeatures ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1410">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.newFeatures ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1411" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1412">
                    <div data-id="element-1413">
                      <div className="font-medium" data-id="element-1414">Marketing Emails</div>
                      <div className="text-sm text-gray-500" data-id="element-1415">
                        Receive promotional content and offers
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  marketingEmails: !notificationSettings.marketingEmails
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.marketingEmails ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1416">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.marketingEmails ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1417" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1418">
                    <div data-id="element-1419">
                      <div className="font-medium" data-id="element-1420">Weekly Digest</div>
                      <div className="text-sm text-gray-500" data-id="element-1421">
                        Get a weekly summary of your activity
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  weeklyDigest: !notificationSettings.weeklyDigest
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.weeklyDigest ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1422">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.weeklyDigest ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1423" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1424">
                    <div data-id="element-1425">
                      <div className="font-medium" data-id="element-1426">Security Alerts</div>
                      <div className="text-sm text-gray-500" data-id="element-1427">
                        Important security notifications
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  securityAlerts: !notificationSettings.securityAlerts
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.securityAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1428">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.securityAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1429" />
                    </button>
                  </label>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1430">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1431">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};